import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../../ui/card";
import { ChevronDown, RefreshCw, TrendingUp } from "lucide-react";

const ActivityChart = ({
  title,
  filter = "Month",
  onFilterChange,
  testMode = false,
}) => {
  const { t, i18n } = useTranslation();
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalSongs, setTotalSongs] = useState(0);

  // Initialize empty data for all months with translated names
  const initializeMonthlyData = () => {
    const months = [
      t("chart.months.jan"),
      t("chart.months.feb"),
      t("chart.months.mar"),
      t("chart.months.apr"),
      t("chart.months.may"),
      t("chart.months.jun"),
      t("chart.months.jul"),
      t("chart.months.aug"),
      t("chart.months.sep"),
      t("chart.months.oct"),
      t("chart.months.nov"),
      t("chart.months.dec"),
    ];
    return months.map((name, index) => ({
      name,
      month: index + 1,
      value: 0,
      percentage: 0,
      songsAdded: 0,
    }));
  };

  // Fetch data and update when language changes
  useEffect(() => {
    const fetchSongsData = async () => {
      setLoading(true);
      setError(null);

      let updatedData = initializeMonthlyData();

      if (testMode) {
        setTimeout(() => {
          const sampleData = [
            { month: 1, totalSongsAdded: 12 },
            { month: 2, totalSongsAdded: 9 },
            { month: 3, totalSongsAdded: 18 },
            { month: 4, totalSongsAdded: 7 },
            { month: 5, totalSongsAdded: 89 },
            { month: 6, totalSongsAdded: 0 },
            { month: 7, totalSongsAdded: 0 },
            { month: 8, totalSongsAdded: 0 },
            { month: 9, totalSongsAdded: 0 },
            { month: 10, totalSongsAdded: 0 },
            { month: 11, totalSongsAdded: 0 },
            { month: 12, totalSongsAdded: 0 },
          ];

          const yearTotal = sampleData.reduce((sum, item) => sum + item.totalSongsAdded, 0);
          setTotalSongs(yearTotal);

          updatedData = updatedData.map((monthItem) => {
            const apiData = sampleData.find((item) => item.month === monthItem.month);
            const songsAdded = apiData?.totalSongsAdded || 0;
            const percentage = yearTotal > 0 ? (songsAdded / yearTotal) * 100 : 0;

            return {
              ...monthItem,
              songsAdded,
              value: percentage,
              percentage: Math.round(percentage * 10) / 10,
            };
          });

          setMonthlyData(updatedData);
          setLoading(false);
        }, 1000);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/api/sync/songs-added-this-month`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();

        if (!result || typeof result !== "object") {
          throw new Error("Response is not a valid JSON object");
        }

        if (result.success === false) {
          throw new Error(result.error || "API returned success: false");
        }

        if (!result.data) {
          throw new Error("No data field in response");
        }

        if (!Array.isArray(result.data)) {
          throw new Error(`Expected array but got ${typeof result.data}: ${JSON.stringify(result.data)}`);
        }

        const yearTotal = result.data.reduce((sum, item) => sum + (item.totalSongsAdded || 0), 0);
        setTotalSongs(yearTotal);

        updatedData = updatedData.map((monthItem) => {
          const apiData = result.data.find((item) => item.month === monthItem.month);
          const songsAdded = apiData?.totalSongsAdded || 0;
          const percentage = yearTotal > 0 ? (songsAdded / yearTotal) * 100 : 0;

          return {
            ...monthItem,
            songsAdded,
            value: percentage,
            percentage: Math.round(percentage * 10) / 10,
          };
        });

        setMonthlyData(updatedData);
      } catch (error) {
        console.error("Error fetching songs data:", error);
        setError(error.message);
        setMonthlyData(updatedData);
      } finally {
        setLoading(false);
      }
    };

    fetchSongsData();
  }, [i18n.language]);

  // Add a custom hook for window size
  function useWindowSize() {
    const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    useEffect(() => {
      const handleResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    return size;
  }

  // Dynamic chart height based on screen width, capped at 500px
  const { width: windowWidth } = useWindowSize();
  const chartHeight = Math.min(
    windowWidth < 400 ? 90 : windowWidth < 640 ? Math.max(windowWidth * 0.3, 100) : windowWidth < 1280 ? 150 : 200,
    500
  );
  const showPercentLabel = windowWidth >= 400;
  const shortMonthLabel = windowWidth < 480;

  return (
    <Card
      className="rounded-2xl border border-slate-200 shadow-md bg-white overflow-hidden w-full max-w-none mx-auto"
      dir="auto"
      style={{ fontFamily: i18n.language === "ar" ? "'Amiri', serif" : "inherit" }}
    >
      <CardContent className="px-3 py-2 sm:px-4 sm:py-4 lg:px-6 lg:py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6 sm:mb-8 lg:mb-10">
          <div>
            <h3 className="text-xs sm:text-sm lg:text-lg font-semibold text-slate-800 mb-2">
              {title || t("chart.title")}
            </h3>
            <p className="text-[10px] sm:text-xs lg:text-sm text-slate-500">
              {t("chart.totalSongs")}: <span className="font-medium text-slate-700">{totalSongs.toLocaleString()}</span>
            </p>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <TrendingUp className="w-2 h-2 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-emerald-500" />
            {loading && <RefreshCw className="w-2 h-2 sm:w-3 sm:h-3 lg:w-4 lg:h-4 text-slate-400 animate-spin" />}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-200 rounded-lg p-2 sm:p-3 lg:p-4 mb-2 sm:mb-3 lg:mb-4">
            <p className="text-red-600 text-[10px] sm:text-xs lg:text-sm">
              {t("chart.error")}: {error}
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center h-24 sm:h-32 lg:h-40">
            <div className="flex flex-col items-center gap-1 sm:gap-2 lg:gap-3">
              <RefreshCw className="w-4 h-4 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-slate-400 animate-spin" />
              <p className="text-slate-500 text-[10px] sm:text-xs lg:text-sm">{t("chart.loading")}</p>
            </div>
          </div>
        ) : (
          /* Chart Container */
          <div className="relative" style={{ height: `${chartHeight}px` }}>
            {/* Y-axis labels */}
            <div
              className={`absolute top-0 h-full flex flex-col justify-between text-[8px] sm:text-[10px] lg:text-xs text-slate-500 font-medium py-1 sm:py-2 lg:py-3 ${
                i18n.language === "ar" ? "right-0" : "left-0"
              }`}
            >
              <span>100%</span>
              <span>75%</span>
              <span>50%</span>
              <span>25%</span>
              <span>0%</span>
            </div>

            {/* Chart Area */}
            <div
              className={`relative h-full px-1 sm:px-0 ${
                i18n.language === "ar"
                  ? "mr-6 sm:mr-8 lg:mr-12 ml-2 sm:ml-4 lg:ml-6"
                  : "ml-6 sm:ml-8 lg:ml-12 mr-1 sm:mr-2 lg:mr-4"
              }`}
            >
              {/* Grid lines */}
              <div
                className={`absolute top-0 h-full flex flex-col justify-between ${
                  i18n.language === "ar"
                    ? "right-6 sm:right-8 lg:right-12 left-2 sm:left-4 lg:left-6"
                    : "left-6 sm:left-8 lg:left-12 right-1 sm:right-2 lg:right-4"
                }`}
              >
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="border-t border-slate-100 w-full" />
                ))}
              </div>

              {/* Bars */}
              <div className="flex items-end justify-between gap-0.5 sm:gap-1 lg:gap-2 h-full mt-20">
                {monthlyData.map((item, index) => {
                  const barHeight = (item.percentage / 100) * chartHeight;
                  const hasData = item.percentage > 0;

                  return (
                    <div key={index} className="flex flex-col items-center relative group flex-1 min-w-0">
                      {/* Tooltip */}
                      <div
                        className={`absolute bottom-full mb-0.5 sm:mb-1 lg:mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 ${
                          i18n.language === "ar" ? "text-right" : "text-left"
                        } hidden sm:block`}
                        style={{ maxWidth: 120, left: '50%', transform: 'translateX(-50%)' }}
                      >
                        <div className="bg-slate-800 text-white text-[8px] sm:text-[10px] lg:text-xs rounded-lg px-1 sm:px-2 lg:px-3 py-0.5 sm:py-1 lg:py-2 whitespace-nowrap">
                          <div className="font-medium">{item.name}</div>
                          <div>
                            {item.songsAdded.toLocaleString()} {t("chart.songs")} ({item.percentage}%)
                          </div>
                        </div>
                        <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 lg:w-2 lg:h-2 bg-slate-800 rotate-45 mx-auto -mt-0.5 sm:-mt-0.5 lg:-mt-1"></div>
                      </div>

                      {/* Percentage label */}
                      {hasData && showPercentLabel && (
                        <div
                          className={`absolute -top-3 sm:-top-4 lg:-top-5 text-[8px] sm:text-[10px] lg:text-xs font-medium text-slate-700`}
                        >
                          {item.percentage}%
                        </div>
                      )}

                      {/* Bar container */}
                      <div className="relative flex flex-col items-center w-full">
                        {/* Background bar */}
                        <div
                          className="w-2 sm:w-3 lg:w-5 bg-slate-100 rounded-t-lg"
                          style={{ height: `${chartHeight}px` }}
                        />
                        {/* Actual bar */}
                        <div
                          className={`w-2 sm:w-3 lg:w-5 rounded-t-lg absolute bottom-0 transition-all duration-500 ease-out ${
                            hasData
                              ? "bg-gradient-to-t from-[#f59e0b] to-[#fbbf24] shadow-md"
                              : "bg-slate-200"
                          }`}
                          style={{
                            height: `${Math.max(barHeight, hasData ? 4 : 0)}px`,
                            boxShadow: hasData ? "0 4px 20px rgba(245, 158, 11, 0.3)" : "none",
                          }}
                        />
                      </div>

                      {/* Month label */}
                      <div className="mt-1 sm:mt-2 lg:mt-3 text-[8px] sm:text-[10px] lg:text-xs font-medium text-slate-600 truncate w-full text-center">
                        {shortMonthLabel ? item.name.slice(0, 3) : item.name}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityChart;